import { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { allCategoryArticles, categoryMeta, type ArticleItem } from '@/mocks/category';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SchemaOrg from '@/components/feature/SchemaOrg';

const tabs = [
  { key: 'drama', label: '影视', icon: 'ri-movie-2-line' },
  { key: 'music', label: '音乐', icon: 'ri-music-2-line' },
  { key: 'variety', label: '综艺', icon: 'ri-emotion-laugh-line' },
  { key: 'news', label: '资讯', icon: 'ri-newspaper-line' },
];

export default function CategoryPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get('tab') || 'drama';
  const [animating, setAnimating] = useState(false);
  const [displayArticles, setDisplayArticles] = useState<ArticleItem[]>([]);

  useEffect(() => {
    const validTab = tabs.find((t) => t.key === activeTab) ? activeTab : 'drama';
    if (validTab !== activeTab) {
      setSearchParams({ tab: validTab }, { replace: true });
      return;
    }
    setAnimating(true);
    const timer = setTimeout(() => {
      setDisplayArticles(allCategoryArticles[validTab] || []);
      setAnimating(false);
    }, 200);
    return () => clearTimeout(timer);
  }, [activeTab, setSearchParams]);

  const switchTab = (key: string) => {
    if (key === activeTab || animating) return;
    setSearchParams({ tab: key });
  };

  const meta = categoryMeta[activeTab as keyof typeof categoryMeta] || categoryMeta.drama;

  return (
    <>
      <SchemaOrg
        type="WebPage"
        data={{
          name: `日韩${meta.name} - 日韩在线`,
          description: meta.description,
          url: `https://rikkan-online.com/category?tab=${activeTab}`,
        }}
      />
      <SchemaOrg
        type="BreadcrumbList"
        data={{
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: '首页', item: 'https://rikkan-online.com' },
            { '@type': 'ListItem', position: 2, name: meta.name, item: `https://rikkan-online.com/category?tab=${activeTab}` },
          ],
        }}
      />

      <main className="min-h-screen pt-16">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <Breadcrumb items={[{ label: meta.name }]} />

          {/* Header */}
          <div className="py-8 md:py-12">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 md:w-12 md:h-12 flex items-center justify-center rounded-xl bg-primary-100">
                <i className={`${meta.icon} text-primary-600 text-xl md:text-2xl`}></i>
              </div>
              <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950">
                日韩{meta.name}
              </h1>
            </div>
            <p className="text-foreground-600 text-sm md:text-base ml-0 md:ml-[60px]">
              {meta.description}
            </p>
          </div>

          {/* Tabs */}
          <div className="flex items-center gap-1.5 bg-background-100 rounded-full p-1 mb-9 overflow-x-auto">
            {tabs.map((tab) => (
              <button
                key={tab.key}
                onClick={() => switchTab(tab.key)}
                className={`flex items-center gap-2 px-4 md:px-5 py-2.5 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                  activeTab === tab.key
                    ? 'bg-background-50 text-foreground-950 font-semibold'
                    : 'text-foreground-600 hover:text-foreground-800'
                }`}
              >
                <i className={`${tab.icon} text-base`}></i>
                {tab.label}
              </button>
            ))}
          </div>

          {/* Articles Grid */}
          <div
            className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6 pb-16 transition-opacity duration-200 ${
              animating ? 'opacity-50' : 'opacity-100'
            }`}
          >
            {displayArticles.map((article) => (
              <Link
                key={article.id}
                to={`/article/${article.id}`}
                className="group bg-background-100 rounded-xl overflow-hidden cursor-pointer transition-all duration-300 hover:bg-background-200/80"
              >
                <div className="relative overflow-hidden aspect-[16/9]">
                  <img
                    src={article.image}
                    alt={article.title}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    title={`${article.title} - 日韩在线`}
                  />
                  <span className="absolute top-3 left-3 px-2.5 py-1 bg-primary-500/90 text-background-50 text-xs font-medium rounded-full">
                    {article.category}
                  </span>
                </div>
                <div className="p-4 md:p-5">
                  <h2 className="text-base md:text-lg font-heading font-bold text-foreground-950 mb-2 line-clamp-2 group-hover:text-primary-600 transition-colors">
                    {article.title}
                  </h2>
                  <p className="text-sm text-foreground-600 line-clamp-2 mb-3">
                    {article.excerpt}
                  </p>
                  <div className="flex items-center flex-wrap gap-2 mb-3">
                    {article.tags.map((tag) => (
                      <span
                        key={tag}
                        className="px-2 py-0.5 text-xs rounded-full bg-secondary-100 text-secondary-700"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                  <div className="flex items-center gap-3 text-xs text-foreground-500">
                    <span>{article.author}</span>
                    <span>·</span>
                    <span>{article.date}</span>
                    <span>·</span>
                    <span>{article.readTime}</span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </main>
    </>
  );
}